import { useCallback, useEffect, useState } from "react";
import { Inbox, Mail, Phone, Users2, Trash2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const STATUS = {
  NEW: { label: "Nuova", cls: "bg-amber-100 text-amber-800" },
  CONTACTED: { label: "Contattata", cls: "bg-sky-100 text-sky-800" },
  CLOSED: { label: "Chiusa", cls: "bg-slate-200 text-slate-600" },
};
const NEXT_ACTIONS = {
  NEW: [["CONTACTED", "Segna contattata"]],
  CONTACTED: [["CLOSED", "Chiudi"], ["NEW", "Riapri"]],
  CLOSED: [["NEW", "Riapri"]],
};

export default function AdminDemoRequests() {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("ALL");
  const [error, setError] = useState(null);
  const [savingId, setSavingId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = useCallback(async () => {
    try {
      const qs = filter === "ALL" ? "" : `?status=${filter}`;
      const { data: d } = await api.get(`/admin/demo-requests${qs}`);
      setData(d);
      setError(null);
    } catch (e) {
      setError(formatApiError(e));
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const setStatus = async (req, status) => {
    setSavingId(req.id);
    try {
      await api.patch(`/admin/demo-requests/${req.id}`, { status });
      toast.success(`Richiesta di ${req.nome} ${req.cognome} → ${STATUS[status].label}`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSavingId(null);
    }
  };

  const hardDelete = async () => {
    setSavingId(deleteTarget.id);
    try {
      await api.delete(`/admin/demo-requests/${deleteTarget.id}`);
      toast.success(`Richiesta di ${deleteTarget.nome} ${deleteTarget.cognome} eliminata definitivamente`);
      setDeleteTarget(null);
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSavingId(null);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-demo-requests-page">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-slate-900">Demo Requests</h1>
          <p className="text-sm text-slate-600 mt-1">
            Lead raccolti dal form "Richiedi una demo" della landing page pubblica.
          </p>
        </div>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-[180px]" data-testid="demo-requests-filter" aria-label="Filtra per stato">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">Tutte</SelectItem>
            {Object.entries(STATUS).map(([k, v]) => (
              <SelectItem key={k} value={k}>{v.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {error && (
        <Card className="border-red-200 bg-red-50" data-testid="demo-requests-error">
          <CardContent className="p-4 text-sm text-red-700">{error}</CardContent>
        </Card>
      )}

      {data && (
        <div className="grid grid-cols-3 gap-4" data-testid="demo-requests-stats">
          {Object.entries(STATUS).map(([k, v]) => (
            <Card key={k} className="border-slate-200" data-testid={`demo-stats-${k.toLowerCase()}`}>
              <CardContent className="p-4 flex items-center justify-between">
                <span className="text-sm text-slate-600">{v.label}</span>
                <span className="text-2xl font-extrabold text-slate-900">{data.stats[k] ?? 0}</span>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Card className="border-slate-200" data-testid="demo-requests-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold text-slate-700 flex items-center gap-2">
            <Inbox className="w-4 h-4" aria-hidden="true" />
            Richieste {data ? `(${data.requests.length})` : ""}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {!data ? (
            <p className="text-sm text-slate-500">Caricamento…</p>
          ) : data.requests.length === 0 ? (
            <p className="text-sm text-slate-600 text-center py-8" data-testid="demo-requests-empty">
              Nessuna richiesta demo{filter !== "ALL" ? " con questo stato" : ""}.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="demo-requests-table">
                <thead>
                  <tr className="text-left text-xs text-slate-500 border-b border-slate-200">
                    <th className="py-2 pr-3 font-medium">Data</th>
                    <th className="py-2 pr-3 font-medium">Contatto</th>
                    <th className="py-2 pr-3 font-medium">Azienda</th>
                    <th className="py-2 pr-3 font-medium">Messaggio</th>
                    <th className="py-2 pr-3 font-medium">Stato</th>
                    <th className="py-2 font-medium">Azioni</th>
                  </tr>
                </thead>
                <tbody>
                  {data.requests.map((r) => (
                    <tr key={r.id} className="border-b border-slate-100 align-top" data-testid={`demo-request-row-${r.id}`}>
                      <td className="py-3 pr-3 whitespace-nowrap text-slate-600">
                        {new Date(r.created_at).toLocaleDateString("it-IT")}
                      </td>
                      <td className="py-3 pr-3">
                        <p className="font-medium text-slate-900">{r.nome} {r.cognome}</p>
                        <p className="text-xs text-slate-500 flex items-center gap-1 mt-0.5">
                          <Mail className="w-3 h-3" aria-hidden="true" />{r.email}
                        </p>
                        {r.telefono && (
                          <p className="text-xs text-slate-500 flex items-center gap-1 mt-0.5">
                            <Phone className="w-3 h-3" aria-hidden="true" />{r.telefono}
                          </p>
                        )}
                      </td>
                      <td className="py-3 pr-3">
                        <p className="text-slate-800">{r.azienda}</p>
                        {r.numero_dipendenti !== null && r.numero_dipendenti !== undefined && (
                          <p className="text-xs text-slate-500 flex items-center gap-1 mt-0.5">
                            <Users2 className="w-3 h-3" aria-hidden="true" />{r.numero_dipendenti} dipendenti
                          </p>
                        )}
                      </td>
                      <td className="py-3 pr-3 max-w-[260px]">
                        <p className="text-xs text-slate-600 line-clamp-3" title={r.messaggio || ""}>
                          {r.messaggio || "—"}
                        </p>
                      </td>
                      <td className="py-3 pr-3">
                        <Badge className={STATUS[r.status]?.cls} data-testid={`demo-request-status-${r.id}`}>
                          {STATUS[r.status]?.label || r.status}
                        </Badge>
                      </td>
                      <td className="py-3">
                        <div className="flex flex-col gap-1.5">
                          {(NEXT_ACTIONS[r.status] || []).map(([target, label]) => (
                            <Button key={target} size="sm" variant="outline"
                              className="h-7 text-xs justify-start"
                              disabled={savingId === r.id}
                              onClick={() => setStatus(r, target)}
                              data-testid={`demo-request-set-${target.toLowerCase()}-${r.id}`}>
                              {label}
                            </Button>
                          ))}
                          <Button size="sm" variant="outline"
                            className="h-7 text-xs justify-start text-red-700 border-red-200 hover:bg-red-50"
                            disabled={savingId === r.id}
                            onClick={() => setDeleteTarget(r)}
                            data-testid={`demo-request-delete-${r.id}`}>
                            <Trash2 className="w-3 h-3 mr-1" aria-hidden="true" /> Elimina
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="demo-request-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare questa richiesta demo?</AlertDialogTitle>
            <AlertDialogDescription>
              La richiesta di {deleteTarget?.nome} {deleteTarget?.cognome} ({deleteTarget?.azienda},{" "}
              {deleteTarget?.email}) verrà eliminata definitivamente.
              L'operazione non è annullabile.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="demo-request-delete-cancel">Annulla</AlertDialogCancel>
            <AlertDialogAction className="bg-red-700 hover:bg-red-800" onClick={hardDelete}
              data-testid="demo-request-delete-confirm">Elimina definitivamente</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
